/**
 * <InlineAISuggestion /> — CTA-actionable card surfaced above a
 * page's DataTable. Distinct from <AiBriefPanel> (which narrates).
 *
 * Per Session 3a §3.2 spec — every Operations second-line screen
 * renders 1-3 of these between the page header and its DataTable.
 *
 * Visual contract:
 *   - Container: bg #f5f3ff · border 1px solid #e9d5ff · radius 10px
 *   - Agent badge: ✦ sparkle (Brand Teal) + agent name (#6b21a8)
 *   - Icon: lucide icon, severity-colored
 *   - Headline: 14px / 600 / #1e293b
 *   - Body: 12px / #475569 / 2-line max
 *   - Primary action: brand-gradient button
 *   - Secondary action: ghost
 *
 * Dismissal: stored in localStorage with key
 * `solvait.suggestions.dismissed.{userId}.{suggestionKey}`. Reappears
 * after 7 days OR when key changes (caller stamps the key to invalidate
 * when underlying data changes).
 */

import { Sparkles, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

export type SuggestionAgent =
  | "tashghil"
  | "rawatib"
  | "mutabaa"
  | "khidmat";

export type SuggestionSeverity = "info" | "warning" | "critical";

const _AGENT_LABELS_EN: Record<SuggestionAgent, string> = {
  tashghil: "Tashghil",
  rawatib: "Rawatib",
  mutabaa: "Mutaba'aa",
  khidmat: "Khidmat",
};

const _AGENT_LABELS_AR: Record<SuggestionAgent, string> = {
  tashghil: "تشغيل",
  rawatib: "رواتب",
  mutabaa: "متابعة",
  khidmat: "خدمات",
};

const _SEVERITY_ICON_COLOR: Record<SuggestionSeverity, string> = {
  info: "text-brand-blue",
  warning: "text-amber-500",
  critical: "text-rose-600",
};

const _DISMISSAL_TTL_DAYS = 7;

function _isStillDismissed(key: string, userId: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(
      `solvait.suggestions.dismissed.${userId}.${key}`,
    );
    if (!raw) return false;
    const at = parseInt(raw, 10);
    if (Number.isNaN(at)) return false;
    const ageDays = (Date.now() - at) / (1000 * 60 * 60 * 24);
    return ageDays < _DISMISSAL_TTL_DAYS;
  } catch {
    return false;
  }
}

function _persistDismissal(key: string, userId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      `solvait.suggestions.dismissed.${userId}.${key}`,
      String(Date.now()),
    );
  } catch {
    // localStorage disabled — silently ignore; the suggestion will
    // reappear on next render. Acceptable.
  }
}

export interface InlineAISuggestionProps {
  /** Which agent attribution badge to render. */
  agent: SuggestionAgent;
  /** Lucide icon node (rendered inline at 16px). */
  icon: ReactNode;
  /** Severity colors the icon. Default 'info'. */
  severity?: SuggestionSeverity;
  /** Main bold line. */
  headline: string;
  /** Sub-line, up to 2 lines. */
  body?: string;
  /** Primary CTA — brand-gradient button. */
  primaryAction: { label: string; onClick: () => void };
  /** Optional secondary CTA — ghost button. */
  secondaryAction?: { label: string; onClick: () => void };
  /** Stable key for dismissal storage. Bump to re-show. */
  suggestionKey: string;
  /** User id (Clerk userId) — scopes the dismissal record. */
  userId?: string;
  /** Optional locale override. Inherited from i18n otherwise. */
  locale?: "en" | "ar";
}

export function InlineAISuggestion({
  agent,
  icon,
  severity = "info",
  headline,
  body,
  primaryAction,
  secondaryAction,
  suggestionKey,
  userId = "anonymous",
  locale,
}: InlineAISuggestionProps) {
  const { t, i18n } = useTranslation();
  const effLocale = locale ?? (i18n.language === "ar" ? "ar" : "en");
  const [dismissed, setDismissed] = useState<boolean>(false);

  useEffect(() => {
    setDismissed(_isStillDismissed(suggestionKey, userId));
  }, [suggestionKey, userId]);

  if (dismissed) return null;

  const agentLabel =
    effLocale === "ar"
      ? _AGENT_LABELS_AR[agent]
      : _AGENT_LABELS_EN[agent];

  return (
    <div
      data-ai-suggestion="true"
      role="note"
      className="mb-3 rounded-[10px] border border-[#e9d5ff] bg-[#f5f3ff] px-[14px] py-[12px]"
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 ${_SEVERITY_ICON_COLOR[severity]}`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Sparkles
              className="h-3 w-3 text-[#00D3C2]"
              aria-hidden="true"
            />
            <span className="text-[11px] font-medium uppercase tracking-wide text-[#6b21a8]">
              {agentLabel}
            </span>
          </div>
          <h3 className="mt-0.5 text-[14px] font-semibold text-[#1e293b]">
            {headline}
          </h3>
          {body && (
            <p className="mt-0.5 line-clamp-2 text-[12px] text-[#475569]">
              {body}
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={primaryAction.onClick}
              className="rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1 text-[12px] font-medium text-white hover:opacity-95"
            >
              {primaryAction.label}
            </button>
            {secondaryAction && (
              <button
                type="button"
                onClick={secondaryAction.onClick}
                className="text-[12px] text-[#64748b] hover:text-[#1e293b]"
              >
                {secondaryAction.label}
              </button>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            _persistDismissal(suggestionKey, userId);
            setDismissed(true);
          }}
          className="text-text-tertiary hover:text-text-secondary"
          aria-label={t("common.dismiss", "Dismiss")}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

export default InlineAISuggestion;
